import { useQuery } from "@tanstack/react-query";
import { cardIssuanceStatusQueryConfig, limitsQueryConfig, isNetworkError } from "../../../../../api";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RoutesMap } from "../../../../../routes.tsx";
import { isPendingCardStatus } from "../../LoadingPage/hooks";
import { useSMSFormState } from "../../SMSForm/hooks";
import { ESMSFormState } from "../../../../../store/slices/smsFormSliceCreator.ts";

/**
 * Polls card issuance status after OTP confirmation success
 * Shows PageLoader while polling and handles navigation based on HTTP response status
 */
export function useB2pCardStatusPolling() {
  const navigate = useNavigate();
  const smsForm = useSMSFormState();
  const lastHandledRef = useRef<string | null>(null);
  const limits = useQuery(limitsQueryConfig());
  const firstLimit = limits.data?.[0];
  const limitId = firstLimit?.id;

  // Only poll after OTP confirmation is successful (formState is submitted)
  const shouldPoll = smsForm.formState === ESMSFormState.submitted && !!limitId;

  const cardIssuanceStatus = useQuery({
    ...cardIssuanceStatusQueryConfig(limitId ?? ""),
    enabled: shouldPoll,
    refetchInterval: (query) => {
      const { data, error } = query.state;

      // Stop polling if we have an error (456 or other)
      if (error) {
        return false;
      }

      if (isPendingCardStatus(data)) {
        return 10000; // 10 seconds
      }
      return false;
    },
    refetchIntervalInBackground: true,
  });

  const handleResponse = useCallback(() => {
    // Check for HTTP errors first - any error status (456, 500, etc.) -> show ErrorPage
    if (cardIssuanceStatus.error) {
      if (isNetworkError(cardIssuanceStatus.error)) {
        // 456 or any other error status -> show ErrorPage
        navigate(RoutesMap.login.loading);
        return;
      }
      // Other errors - also show ErrorPage
      navigate(RoutesMap.login.loading);
      return;
    }

    // Success (HTTP 200) - check the card status value
    if (cardIssuanceStatus.isSuccess) {
      const status = cardIssuanceStatus.data;

      // Continue polling for pending statuses (NEW, PENDING, OTP_REQUIRED, WAITING_FOR_ACTIVATION)
      if (isPendingCardStatus(status)) {
        return;
      }

      // Only navigate to main page when status is ACTIVE
      if (status === "ACTIVE") {
        navigate(RoutesMap.index);
        return;
      }

      // For other final statuses (ERROR, etc.), stay on page
      // ERROR should be handled by parent component or show error
    }
  }, [cardIssuanceStatus.error, cardIssuanceStatus.isSuccess, cardIssuanceStatus.data, navigate]);

  useEffect(() => {
    // Only handle response when query has completed (either success or error)
    if (!shouldPoll) return;

    if (cardIssuanceStatus.isSuccess || cardIssuanceStatus.isError) {
      const status = cardIssuanceStatus.isSuccess ? cardIssuanceStatus.data : "ERROR";
      const errorKey = cardIssuanceStatus.isError
        ? (isNetworkError(cardIssuanceStatus.error)
            ? `network:${(cardIssuanceStatus.error as unknown as { status?: number }).status ?? "unknown"}`
            : "error")
        : "ok";
      // eslint-disable-next-line
      const handledKey = `${limitId ?? ""}:${errorKey}:${status}`;
      if (lastHandledRef.current === handledKey) {
        return;
      }
      lastHandledRef.current = handledKey;

      handleResponse();
    }
  }, [shouldPoll, cardIssuanceStatus.isSuccess, cardIssuanceStatus.isError, cardIssuanceStatus.data, cardIssuanceStatus.error, handleResponse]);

  // Show loader while fetching or when status is pending (and no error)
  const isPolling = shouldPoll && (
    cardIssuanceStatus.isFetching
    || (cardIssuanceStatus.isSuccess && (isPendingCardStatus(cardIssuanceStatus.data)))
  ) && !cardIssuanceStatus.isError;

  return {
    cardIssuanceStatus,
    isPolling,
  };
}
